import { useCallback, useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Card, CardHeader } from "@/components/ui/Card";
import { Label } from "@/components/ui/Label";
import { toast } from "sonner";
import {
  formatPhoneDisplay,
  PHONE_MASK,
  zipDisplay,
  ZIP_MASK,
  DOB_MASK,
} from "@/hooks/useDemographicsForm";
import { cn } from "@/utils/cn";

const ERROR_INPUT_CLASS = "border-red-500 focus:ring-red-500";

// Below sm the section card is flattened onto the page background so the fields run to the
// page gutter; overflow stays visible so input focus rings at the edge are not clipped.
// `!` beats Card's inline shadow.
const FLAT_BELOW_SM =
  "max-sm:overflow-visible max-sm:rounded-none max-sm:border-0 max-sm:bg-transparent max-sm:!shadow-none";

// Read-only fields: compact label/value rows with hairline dividers below sm (a long value wraps
// under its label); stacked label over value in the grid from sm.
const READONLY_ROW_CLASS =
  "flex min-h-11 min-w-0 flex-wrap items-center justify-between gap-x-4 border-b border-[var(--hs-line)] py-2 sm:block sm:min-h-0 sm:border-0 sm:py-0";
const READONLY_VALUE_CLASS =
  "ml-auto min-w-0 break-words text-right text-[15px] font-bold text-healthsource-dark sm:ml-0 sm:text-left";

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return <p className="mt-1 text-xs font-semibold text-red-600">{message}</p>;
}

const PERSONAL_FIELDS = [
  "firstName", "mi", "lastName", "nickname", "dob", "sex",
  "maritalStatID", "address", "address2", "city", "state", "zip",
  "mobilePhone", "homePhone", "email", "contactMethod", "employStatID",
] as const;

export interface DemographicsPersonalSectionProps {
  patient: Record<string, string>;
  update: (field: string, value: string) => void;
  errors?: Record<string, string>;
  onSave: () => Promise<boolean>;
  revertFields: (fields: Record<string, string>) => void;
  onDirtyChange?: (dirty: boolean) => void;
  /** When set, edit state is controlled by parent (e.g. profile tab bar). */
  editingOverride?: boolean;
  /** When true, do not render Edit/Cancel/Update in the card header. */
  hideHeaderActions?: boolean;
  sexOptions: { value: string; label: string }[];
  stateOptions: { value: string; label: string }[];
  maritalOptions: { value: string; label: string }[];
  contactPrefOptions: { value: string; label: string }[];
  employmentOptions: { value: string; label: string }[];
  /** Wizard/mobile safe layout: render one field per row. */
  singleColumn?: boolean;
}

export function DemographicsPersonalSection({
  patient,
  update,
  errors,
  onSave,
  revertFields,
  onDirtyChange,
  editingOverride,
  hideHeaderActions,
  sexOptions,
  stateOptions,
  maritalOptions,
  contactPrefOptions,
  employmentOptions,
  singleColumn = false,
}: DemographicsPersonalSectionProps) {
  const [internalEditing, setInternalEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const snapshotRef = useRef<Record<string, string>>({});

  const editing = editingOverride !== undefined ? editingOverride : internalEditing;

  const snapshot = useCallback(() => {
    const s: Record<string, string> = {};
    for (const k of PERSONAL_FIELDS) s[k] = patient[k] ?? "";
    return s;
  }, [patient]);

  useEffect(() => {
    if (editingOverride === true) snapshotRef.current = snapshot();
  }, [editingOverride, snapshot]);

  const isDirty = editing && PERSONAL_FIELDS.some(
    (k) => (patient[k] ?? "") !== (snapshotRef.current[k] ?? "")
  );

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const startEditing = () => {
    snapshotRef.current = snapshot();
    if (editingOverride === undefined) setInternalEditing(true);
  };

  const revert = () => {
    revertFields(snapshotRef.current);
  };

  const handleCancel = () => {
    if (isDirty) {
      setShowDiscardModal(true);
    } else {
      if (editingOverride === undefined) setInternalEditing(false);
    }
  };

  const confirmDiscard = () => {
    revert();
    setShowDiscardModal(false);
    if (editingOverride === undefined) setInternalEditing(false);
  };

  const handleUpdate = async () => {
    setSaving(true);
    try {
      await onSave();
      if (editingOverride === undefined) setInternalEditing(false);
    } catch {
      toast.error("Failed to update patient info.");
    } finally {
      setSaving(false);
    }
  };

  const mobileReadonly = formatPhoneDisplay(
    (patient.mobilePhone ?? "").replace(/\D/g, "")
  );
  const homeReadonly = formatPhoneDisplay(
    (patient.homePhone ?? "").replace(/\D/g, "")
  );

  return (
    <>
      <Card className={cn("mb-3", FLAT_BELOW_SM)}>
        <CardHeader
          className="max-sm:px-0"
          action={
            hideHeaderActions ? null : !editing ? (
              <button
                type="button"
                onClick={startEditing}
                className="flex min-h-11 items-center gap-1.5 px-3 text-sm font-semibold text-healthsource-navy transition-colors hover:text-healthsource-red"
              >
                <i className="fas fa-pen" />
                Edit
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="flex min-h-11 items-center gap-1.5 px-3 text-sm font-semibold text-healthsource-gray transition-colors hover:text-healthsource-red"
                >
                  Cancel
                </button>
                {isDirty && (
                  <button
                    type="button"
                    onClick={handleUpdate}
                    disabled={saving}
                    className="min-h-11 rounded-md px-3 text-sm font-bold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
                    style={{ backgroundColor: "var(--hs-red)" }}
                  >
                    {saving ? "Saving…" : "Update"}
                  </button>
                )}
              </div>
            )
          }
        >
          Personal Information
        </CardHeader>

        {!editing ? (
          <div className="py-1 sm:space-y-3 sm:p-5">
            <div
              className="grid grid-cols-1 sm:grid-cols-3 sm:gap-x-6 sm:gap-y-3 md:grid-cols-4"
            >
              <Field label="First Name" value={patient.firstName} />
              <Field label="MI" value={patient.mi} />
              <Field label="Last Name" value={patient.lastName} />
              <Field label="Preferred Name" value={patient.nickname} />
            </div>
            <div
              className="grid grid-cols-1 sm:grid-cols-3 sm:gap-x-6 sm:gap-y-3 md:grid-cols-4"
            >
              <Field label="Date of Birth" value={patient.dob} />
              <Field label="Gender" value={patient.sex} />
              <Field label="Mobile Phone" value={mobileReadonly} />
              <Field label="Home Phone" value={homeReadonly} />
            </div>
            <div
              className="grid grid-cols-1 sm:grid-cols-3 sm:gap-x-6 sm:gap-y-3 md:grid-cols-5"
            >
              <Field label="Street Address" value={patient.address} />
              <Field label="Address Line 2" value={patient.address2} />
              <Field label="City" value={patient.city} />
              <Field label="State" value={patient.state} />
              <Field label="Zip" value={zipDisplay(patient.zip)} />
            </div>
            <div
              className="grid grid-cols-1 sm:grid-cols-3 sm:gap-x-6 sm:gap-y-3 md:grid-cols-4"
            >
              <Field label="Email" value={patient.email} />
              <Field label="Contact Preference" value={patient.contactMethod} />
              <Field
                label="Marital Status"
                value={maritalOptions.find((o) => o.value === (patient.maritalStatID ?? ""))?.label ?? patient.maritalStatID}
              />
              <Field
                label="Employment"
                value={
                  employmentOptions.find(
                    (o) => o.value === String(patient.employStatID ?? "")
                  )?.label
                }
              />
            </div>
          </div>
        ) : (
          <div className="space-y-4 py-3 sm:p-5">
            <div
              className={
                singleColumn
                  ? "grid min-w-0 grid-cols-1 gap-3 md:grid-cols-[1.25fr_0.35fr_1.25fr_1.25fr_0.95fr_minmax(5.25rem,0.52fr)] md:gap-2.5"
                  : "grid min-w-0 grid-cols-2 gap-3 md:grid-cols-[1.25fr_0.35fr_1.25fr_1.25fr_0.95fr_minmax(5.25rem,0.52fr)] md:gap-2.5"
              }
            >
              <div className="min-w-0">
                <Label htmlFor="demographics-firstName" required>First Name</Label>
                <Input
                  id="demographics-firstName"
                  placeholder="First Name"
                  value={patient.firstName}
                  onChange={(e) => update("firstName", e.target.value)}
                  className={cn(errors?.firstName && ERROR_INPUT_CLASS)}
                  aria-invalid={errors?.firstName ? true : undefined}
                  required
                />
                <FieldError message={errors?.firstName} />
              </div>
              <div className="min-w-0">
                <Label htmlFor="demographics-mi">MI</Label>
                <Input id="demographics-mi" className="text-center" placeholder="MI" maxLength={1} value={patient.mi} onChange={(e) => update("mi", e.target.value.slice(0, 1))} />
              </div>
              <div className="min-w-0">
                <Label htmlFor="demographics-lastName" required>Last Name</Label>
                <Input
                  id="demographics-lastName"
                  placeholder="Last Name"
                  value={patient.lastName}
                  onChange={(e) => update("lastName", e.target.value)}
                  className={cn(errors?.lastName && ERROR_INPUT_CLASS)}
                  aria-invalid={errors?.lastName ? true : undefined}
                  required
                />
                <FieldError message={errors?.lastName} />
              </div>
              <div className="min-w-0">
                <Label htmlFor="demographics-nickname">Preferred Name</Label>
                <Input id="demographics-nickname" placeholder="Preferred Name" value={patient.nickname} onChange={(e) => update("nickname", e.target.value)} />
              </div>
              <div className="min-w-0">
                <Label htmlFor="demographics-dob" required>Date of Birth</Label>
                <Input
                  id="demographics-dob"
                  inputMode="numeric"
                  placeholder="MM/DD/YYYY"
                  value={patient.dob}
                  onChange={(e) => update("dob", DOB_MASK(e.target.value))}
                  className={cn(errors?.dob && ERROR_INPUT_CLASS)}
                  aria-invalid={errors?.dob ? true : undefined}
                  required
                />
                <FieldError message={errors?.dob} />
              </div>
              <div className="min-w-0">
                <Label htmlFor="demographics-sex" required>Gender</Label>
                <Select
                  id="demographics-sex"
                  className={cn("w-full", errors?.sex && ERROR_INPUT_CLASS)}
                  value={patient.sex}
                  onChange={(e) => update("sex", e.target.value)}
                  aria-invalid={errors?.sex ? true : undefined}
                  required
                >
                  <option value="">Select</option>
                  {sexOptions.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
                </Select>
                <FieldError message={errors?.sex} />
              </div>
            </div>

            <div
              className={
                singleColumn
                  ? "grid min-w-0 grid-cols-1 gap-3 md:grid-cols-2 md:gap-2.5"
                  : "grid min-w-0 grid-cols-1 gap-3 md:grid-cols-2 md:gap-2.5"
              }
            >
              <div className="min-w-0">
                <Label htmlFor="demographics-address" required>Street Address</Label>
                <Input
                  id="demographics-address"
                  placeholder="Street Address"
                  value={patient.address}
                  onChange={(e) => update("address", e.target.value)}
                  className={cn(errors?.address && ERROR_INPUT_CLASS)}
                  aria-invalid={errors?.address ? true : undefined}
                  required
                />
                <FieldError message={errors?.address} />
              </div>
              <div className="min-w-0">
                <Label htmlFor="demographics-address2">Address Line 2</Label>
                <Input id="demographics-address2" placeholder="Address Line 2" value={patient.address2} onChange={(e) => update("address2", e.target.value)} />
              </div>
            </div>

            <div
              className={
                singleColumn
                  ? "grid min-w-0 grid-cols-1 gap-3 md:grid-cols-[minmax(0,1fr)_minmax(5.5rem,7rem)_minmax(6.5rem,9rem)] md:gap-2.5"
                  : "grid min-w-0 grid-cols-1 gap-3 md:grid-cols-[minmax(0,1fr)_minmax(5.5rem,7rem)_minmax(6.5rem,9rem)] md:gap-2.5"
              }
            >
              <div className="min-w-0 md:min-w-[8rem]">
                <Label htmlFor="demographics-city" required>City</Label>
                <Input
                  id="demographics-city"
                  placeholder="City"
                  value={patient.city}
                  onChange={(e) => update("city", e.target.value)}
                  className={cn(errors?.city && ERROR_INPUT_CLASS)}
                  aria-invalid={errors?.city ? true : undefined}
                  required
                />
                <FieldError message={errors?.city} />
              </div>
              <div className="min-w-0">
                <Label htmlFor="demographics-state" required>State</Label>
                <Select
                  id="demographics-state"
                  className={cn(errors?.state && ERROR_INPUT_CLASS)}
                  value={patient.state}
                  onChange={(e) => update("state", e.target.value)}
                  aria-invalid={errors?.state ? true : undefined}
                  required
                >
                  <option value="">Select</option>
                  {stateOptions.map((o, i) => (<option key={`${o.value}-${i}`} value={o.value}>{o.label}</option>))}
                </Select>
                <FieldError message={errors?.state} />
              </div>
              <div className="min-w-0">
                <Label htmlFor="demographics-zip" required>Zip</Label>
                <Input
                  id="demographics-zip"
                  inputMode="numeric"
                  autoComplete="postal-code"
                  placeholder="XXXXX-XXXX"
                  value={zipDisplay(patient.zip)}
                  onChange={(e) => update("zip", ZIP_MASK(e.target.value))}
                  className={cn(errors?.zip && ERROR_INPUT_CLASS)}
                  aria-invalid={errors?.zip ? true : undefined}
                  required
                />
                <FieldError message={errors?.zip} />
              </div>
            </div>

            <div
              className={
                singleColumn
                  ? "grid min-w-0 grid-cols-1 gap-3 md:grid-cols-4 md:gap-2.5"
                  : "grid min-w-0 grid-cols-1 gap-3 md:grid-cols-4 md:gap-2.5"
              }
            >
              <div className="min-w-0">
                <Label htmlFor="demographics-mobilePhone">Mobile Phone</Label>
                <Input
                  id="demographics-mobilePhone"
                  type="tel"
                  placeholder="(XXX) XXX-XXXX"
                  value={patient.mobilePhone}
                  onChange={(e) => update("mobilePhone", formatPhoneDisplay(PHONE_MASK(e.target.value)))}
                  className={cn(errors?.mobilePhone && ERROR_INPUT_CLASS)}
                  aria-invalid={errors?.mobilePhone ? true : undefined}
                />
                <FieldError message={errors?.mobilePhone} />
              </div>
              <div className="min-w-0">
                <Label htmlFor="demographics-homePhone">Home Phone</Label>
                <Input
                  id="demographics-homePhone"
                  type="tel"
                  placeholder="(XXX) XXX-XXXX"
                  value={patient.homePhone}
                  onChange={(e) => update("homePhone", formatPhoneDisplay(PHONE_MASK(e.target.value)))}
                  className={cn(errors?.homePhone && ERROR_INPUT_CLASS)}
                  aria-invalid={errors?.homePhone ? true : undefined}
                />
                <FieldError message={errors?.homePhone} />
              </div>
              <div className="min-w-0">
                <Label htmlFor="demographics-email" required>Email</Label>
                <Input
                  id="demographics-email"
                  type="email"
                  placeholder="Email"
                  value={patient.email}
                  onChange={(e) => update("email", e.target.value)}
                  className={cn(errors?.email && ERROR_INPUT_CLASS)}
                  aria-invalid={errors?.email ? true : undefined}
                  required
                />
                <FieldError message={errors?.email} />
              </div>
              <div className="min-w-0">
                <Label htmlFor="demographics-contactMethod">Contact Preference</Label>
                <Select id="demographics-contactMethod" value={patient.contactMethod} onChange={(e) => update("contactMethod", e.target.value)}>
                  <option value="">Select</option>
                  {contactPrefOptions.map((o, i) => (<option key={`${o.value}-${i}`} value={o.value}>{o.label}</option>))}
                </Select>
              </div>
            </div>

            <div
              className={
                singleColumn
                  ? "grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-2 md:max-w-3xl md:gap-2.5"
                  : "grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-2 md:max-w-3xl md:gap-2.5"
              }
            >
              <div className="min-w-0">
                <Label htmlFor="demographics-maritalStatID">Marital Status</Label>
                <Select id="demographics-maritalStatID" value={patient.maritalStatID} onChange={(e) => update("maritalStatID", e.target.value)}>
                  <option value="">Select</option>
                  {maritalOptions.map((o, i) => (<option key={`${o.value}-${i}`} value={o.value}>{o.label}</option>))}
                </Select>
              </div>
              <div className="min-w-0">
                <Label htmlFor="demographics-employStatID">Employment</Label>
                <Select id="demographics-employStatID" value={patient.employStatID} onChange={(e) => update("employStatID", e.target.value)}>
                  <option value="">Select</option>
                  {employmentOptions.map((o, i) => (<option key={`${o.value}-${i}`} value={o.value}>{o.label}</option>))}
                </Select>
              </div>
            </div>
          </div>
        )}
      </Card>

      {showDiscardModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true">
          <div className="max-h-[90dvh] w-full max-w-sm overflow-y-auto rounded-xl bg-white p-6 shadow-lg" style={{ boxShadow: "0 8px 32px rgba(30,37,43,0.18)" }}>
            <h3 className="text-[15px] font-extrabold text-healthsource-dark">Unsaved Changes</h3>
            <p className="mt-2 text-[13px] text-healthsource-gray">You have unsaved changes. Are you sure you want to discard them?</p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowDiscardModal(false)}
                className="min-h-11 rounded-md border border-healthsource-lightLavender px-4 text-[13px] font-bold text-healthsource-dark transition-colors hover:bg-healthsource-lightGray"
              >
                Keep Editing
              </button>
              <button
                type="button"
                onClick={confirmDiscard}
                className="min-h-11 rounded-md px-4 text-[13px] font-bold text-white transition-opacity hover:opacity-90"
                style={{ backgroundColor: "var(--hs-red)" }}
              >
                Discard Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Field({ label, value }: { label: string; value?: string }) {
  return (
    <div className={READONLY_ROW_CLASS}>
      <p className="text-[13px] font-semibold text-healthsource-gray">{label}</p>
      <p className={READONLY_VALUE_CLASS}>{value || "—"}</p>
    </div>
  );
}
